import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import LocomotiveScroll from "locomotive-scroll";

import "locomotive-scroll/dist/locomotive-scroll.css";
import "./logoZoomer.scss"; // your CSS file with all styles below
import HorizontalScrollProduct from "../horizontalScrollProduct/HorizontalScrollProduct";
import ProductBox from "../productBox/ProductBox";
import MostPopular from "../mostPopular/MostPopular";
import HomeNewProducts from "../homeNewProducts/HomeNewProducts";

gsap.registerPlugin(ScrollTrigger);

const LogoZoomer = () => {
  const mainRef = useRef(null);
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const images = useRef([]);
  const imageSeq = { frame: 1 };
  const frameCount = 51; // from 50 to 100 inclusive

  // Return image path for frame index
  function files(index) {
    let data = "";
    for (let i = 50; i <= 100; i++) {
      let frameNumber = String(i).padStart(4, "0");
      data += `/text-white/frame_${frameNumber}.png\n`; // note leading slash
    }
    return data.split("\n")[index];
  }

  // Resize canvas and render
  const handleResize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    render();
  };

  // Render the current frame on canvas
  const render = () => {
    const ctx = contextRef.current;
    if (!ctx) return;

    const img = images.current[imageSeq.frame];
    if (!img || !img.complete) return;

    const canvas = ctx.canvas;
    const hRatio = canvas.width / img.width;
    const vRatio = canvas.height / img.height;
    const ratio = Math.max(hRatio, vRatio);
    const centerShift_x = (canvas.width - img.width * ratio) / 2;
    const centerShift_y = (canvas.height - img.height * ratio) / 2;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      img,
      0,
      0,
      img.width,
      img.height,
      centerShift_x,
      centerShift_y,
      img.width * ratio,
      img.height * ratio
    );
  };

  // Setup locomotive scroll and GSAP animation
  const setupScroll = () => {
    const locoScroll = new LocomotiveScroll({
      el: mainRef.current,
      smooth: true,
    });

    locoScroll.on("scroll", ScrollTrigger.update);

    ScrollTrigger.scrollerProxy(mainRef.current, {
      scrollTop(value) {
        return arguments.length
          ? locoScroll.scrollTo(value, 0, 0)
          : locoScroll.scroll.instance.scroll.y;
      },
      getBoundingClientRect() {
        return {
          top: 0,
          left: 0,
          width: window.innerWidth,
          height: window.innerHeight,
        };
      },
      pinType: mainRef.current.style.transform ? "transform" : "fixed",
    });

    // Animate frame sequence on scroll
    gsap.to(imageSeq, {
      frame: frameCount - 1,
      snap: "frame",
      ease: "none",
      scrollTrigger: {
        scrub: 1,
        trigger: canvasRef.current,
        start: "top top",
        end: "600% top",
        scroller: mainRef.current,
        pin: true,
        // markers: true,
      },
      onUpdate: render,
    });

    ScrollTrigger.addEventListener("refresh", () => locoScroll.update());
    ScrollTrigger.refresh();

    return locoScroll;
  };

  useEffect(() => {
    // Set up canvas size and context
    const canvas = canvasRef.current;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    contextRef.current = canvas.getContext("2d");

    // Load all images
    let loadedCount = 0;
    for (let i = 0; i < frameCount; i++) {
      const img = new Image();
      img.src = files(i);
      img.onload = () => {
        loadedCount++;
        if (loadedCount === frameCount) {
          // Render first frame once all loaded
          render();
          // Setup scroll after images loaded
          locoScrollInstance = setupScroll();
        }
      };
      img.onerror = () => {
        console.error(`Failed to load image: ${img.src}`);
      };
      images.current.push(img);
    }

    // Resize listener
    window.addEventListener("resize", handleResize);

    let locoScrollInstance;

    return () => {
      window.removeEventListener("resize", handleResize);
      if (locoScrollInstance) locoScrollInstance.destroy();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div id="main" ref={mainRef}>
      <div id="page">
        <canvas ref={canvasRef}></canvas>
      </div>
      <div id="page1"></div>
      <div id="page2"></div>
      <div id="page3"></div>
      <div id="page3"></div>
      <div id="page3"></div>
      <div id="page3"></div>

      <HorizontalScrollProduct />
      <ProductBox />
      <MostPopular />
    </div>
  );
};

export default LogoZoomer;
